import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  ColumnLayout,
  Form,
  FormField,
  Icon,
  Input,
  InputProps,
  SpaceBetween,
} from '@cloudscape-design/components';
import { GameStarted as IGameStarted } from '../../api';
import { useAutoWakeLock } from '../../hooks/autoWakeLock';
import { Key } from '../../utils/keyCodes';

export interface GameStartedProps {
  game: IGameStarted;
  onGameFinish?: () => void;
  onNextPlayer?: (points: number) => void;
}

export const GameStarted: React.FC<GameStartedProps> = ({
  game,
  onGameFinish,
  onNextPlayer,
}) => {
  const [points, setPoints] = useState('0');
  const [error, setError] = useState('');
  const inputReference = useRef<InputProps.Ref>(null);
  useAutoWakeLock();

  const submit = () => {
    if (error !== '') {
      return;
    }
    if (points.trim() === '') {
      return;
    }
    onNextPlayer?.(parseInt(points));
    setError('');
    setPoints('');
    inputReference?.current?.focus();
  };

  return (
    <>
      <ColumnLayout columns={2} borders="vertical">
        <Form
          actions={
            <SpaceBetween size="s" direction="horizontal">
              <Button
                variant="primary"
                onClick={() => {
                  submit();
                }}
              >
                Next player
              </Button>
              <Button variant="normal" onClick={() => onGameFinish?.()}>
                Finish game
              </Button>
            </SpaceBetween>
          }
        >
          <FormField errorText={error} label="Add points">
            <Input
              ref={inputReference}
              autoFocus
              type="number"
              onKeyDown={(event) => {
                if (event.detail.keyCode === Key.Enter) {
                  submit();
                }
              }}
              onChange={(event) => {
                const number = parseInt(event.detail.value);
                if (Number.isNaN(number) && event.detail.value.trim() === '') {
                  setPoints(event.detail.value);
                  setError('Please input valid number');
                  return;
                }
                setPoints(event.detail.value);
                setError('');
              }}
              value={points}
            />
          </FormField>
        </Form>
        <Box variant="div">
          {game.players.map((player, idx) => (
            <Box key={player.name}>
              {idx === game.currentPlayer && (
                <Icon variant="success" name="angle-right-double" />
              )}{' '}
              <Box
                variant="span"
                padding={{ left: idx === game.currentPlayer ? undefined : 'l' }}
              >
                {player.name}:
              </Box>{' '}
              <Box variant="strong">{game.scores[player.name].points}</Box>
            </Box>
          ))}
        </Box>
      </ColumnLayout>
    </>
  );
};
